// components/ui/Avatar.tsx — Smart avatar: image → monogram → ghost silhouette

import { useState } from 'react';
import clsx from 'clsx';
import { getInitials } from '@/utils/avatarHelpers';
import styles from './Avatar.module.css';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

// Ghost silhouette SVG (used for unknown/unclaimed nodes)
function GhostSilhouette() {
  return (
    <svg viewBox="0 0 40 40" fill="none" width="60%" height="60%" aria-hidden="true">
      <circle cx="20" cy="15" r="7" fill="currentColor" />
      <path
        d="M6 35c0-7.732 6.268-14 14-14s14 6.268 14 14"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export interface AvatarProps {
  name?: string | undefined;
  src?: string | null | undefined;
  size?: AvatarSize | undefined;
  isSelf?: boolean | undefined;
  isDeceased?: boolean | undefined;
  isGhost?: boolean | undefined; // Unknown/unclaimed node
  showStatus?: boolean | undefined;
  isOnline?: boolean | undefined;
  className?: string | undefined;
  alt?: string | undefined;
}

export function Avatar({
  name,
  src,
  size = 'md',
  isSelf = false,
  isDeceased = false,
  isGhost = false,
  showStatus = false,
  isOnline = false,
  className,
  alt,
}: AvatarProps) {
  const [imgError, setImgError] = useState(false);
  const showImage = !!src && !imgError;
  const showMonogram = !showImage && !!name && !isGhost;

  return (
    <span
      className={clsx(
        styles.root,
        styles[size],
        isGhost && styles.ghost,
        isSelf && styles.selfRing,
        isDeceased && styles.deceased,
        className
      )}
      role="img"
      aria-label={alt ?? name ?? 'Unknown person'}
    >
      {showImage && (
        <img
          src={src}
          alt={alt ?? name ?? ''}
          className={styles.image}
          onError={() => setImgError(true)}
          loading="lazy"
        />
      )}

      {showMonogram && (
        <span className={styles.monogram} aria-hidden="true">
          {getInitials(name)}
        </span>
      )}

      {isGhost && <GhostSilhouette />}

      {showStatus && (
        <span
          className={clsx(styles.status, isOnline ? styles.online : styles.offline)}
          aria-label={isOnline ? 'Online' : 'Offline'}
        />
      )}
    </span>
  );
}
